import React, { useEffect, useMemo, useRef, useState } from "react";
import { executiveCouncilApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import PhotoUpload from "../../components/shared/PhotoUpload/PhotoUpload";
import "./ExecutiveCouncil.scss";

const defaultForm = {
  _id: null, Media: "", Full_Name: "", Role_in_Council: "", Short_Description: "",
  Qualification: "", Experience: "", Expertise_Area: "", Email: "", Phone: "",
  Office_Address: "", Display_Order: "", Status: "Active",
};

const roleInCouncilOptions = [
  "Chairperson", "Vice Chairperson", "Member", "Secretary", "External Member",
  "Government Nominee", "Academic Member", "Industry Expert", "Other",
];

const ExecutiveCouncil = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [modalType, setModalType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiClient(executiveCouncilApi);
      const result = await response.json();
      if (response.ok) { setData(result.data || []); }
    } catch (error) {
      console.error("Error fetching Executive Council members:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchName = (item.Full_Name || "").toLowerCase().includes(searchName.toLowerCase());
      const matchRole = (item.Role_in_Council || "").toLowerCase().includes(searchRole.toLowerCase());
      const matchStatus = filterStatus === "all" || item.Status === filterStatus;
      return matchName && matchRole && matchStatus;
    });
  }, [data, searchName, searchRole, filterStatus]);

  useEffect(() => {
    const handleOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 190 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); setModalType("add"); setDropdownOpenId(null); };
  const openViewModal = (item) => { setSelectedItem(item); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (item) => { setFormData({ ...item }); setSelectedFile(null); setPreviewUrl(item.Media || null); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); setSelectedFile(null); setPreviewUrl(null); };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, Media: "" }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? executiveCouncilApi : `${executiveCouncilApi}/${formData._id}`;

      const dataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== "Media" && key !== "_id" && formData[key] !== null && formData[key] !== undefined) {
          dataToSend.append(key, formData[key]);
        }
      });
      if (selectedFile) {
        dataToSend.append("Media", selectedFile);
      } else if (formData.Media) {
        dataToSend.append("Media", formData.Media);
      } else {
        dataToSend.append("Media", "");
      }

      const response = await apiFormDataClient(url, { method, body: dataToSend });
      if (response.ok) { 
        fetchMembers(); 
        closeModal(); 
      } else {
        const errorText = await response.text();
        console.error("API Error Response:", response.status, errorText);
        alert(`Failed to save member. Error: ${response.status}\n${errorText}`);
      }
    } catch (error) {
      console.error("Error saving member:", error);
      alert(`Network or server error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      setLoading(true);
      const item = data.find(m => m._id === id);
      const newStatus = item.Status === "Active" ? "Inactive" : "Active";
      await apiClient(`${executiveCouncilApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...item, Status: newStatus }) });
      fetchMembers(); setConfirmId(null);
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${executiveCouncilApi}/${id}`, { method: "DELETE" });
      fetchMembers(); setConfirmId(null);
    } catch (error) {
      console.error("Error deleting member:", error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  return (
    <div className="ec-page">
      <div className="ec-header">
        <div><h1>Executive Council</h1><p>Manage executive council members, their roles, contact details, and visibility status.</p></div>
        <button type="button" className="ec-add-btn" onClick={openAddModal}><span>+</span> Add Member</button>
      </div>

      <div className="ec-filter-card">
        <div className="ec-filter-grid">
          <div className="form-group"><label>Search by Name</label><input type="text" placeholder="Enter name" value={searchName} onChange={(e) => setSearchName(e.target.value)} /></div>
          <div className="form-group"><label>Search by Role</label><input type="text" placeholder="Enter role" value={searchRole} onChange={(e) => setSearchRole(e.target.value)} /></div>
          <div className="form-group"><label>Filter by Status</label><select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}><option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="ec-table-card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Photo</th><th>Name</th><th>Role in Council</th><th>Status</th><th className="actions-col">Actions</th></tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item._id}>
                    <td><div className="table-photo">{item.Media ? <img src={item.Media} alt="" /> : <div className="photo-placeholder">{(item.Full_Name || "?").charAt(0)}</div>}</div></td>
                    <td><div className="name-cell"><strong>{item.Full_Name}</strong>{item.Short_Description && <span>{item.Short_Description}</span>}</div></td>
                    <td><span className="role-badge">{item.Role_in_Council}</span></td>
                    <td><span className={`status-badge ${item.Status}`}>{item.Status === "Active" ? "Active" : "Inactive"}</span></td>
                    <td className="actions-cell"><button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, item._id)}>⋮</button></td>
                  </tr>
                ))
              ) : (<tr><td colSpan="5" className="empty-cell">No members found.</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {dropdownOpenId !== null && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(data.find(i => i._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg> View
          </button>
          <button type="button" onClick={() => openEditModal(data.find(i => i._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg> Edit
          </button>
          {data.find(i => i._id === dropdownOpenId)?.Status === "Active" ? (
            <button type="button" className="deactivate" onClick={() => openConfirmModal(dropdownOpenId, "deactivate")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" /></svg> Deactivate</button>
          ) : (
            <button type="button" className="activate" onClick={() => openConfirmModal(dropdownOpenId, "activate")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg> Activate</button>
          )}
          <button type="button" className="delete" onClick={() => openConfirmModal(dropdownOpenId, "delete")}><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg> Delete</button>
        </div>
      )}

      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              {confirmAction === "delete" ? <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg> : <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
            </div>
            <h3>{confirmAction === "delete" ? "Delete Member?" : confirmAction === "deactivate" ? "Deactivate Member?" : "Activate Member?"}</h3>
            <p>{confirmAction === "delete" ? "This will permanently remove the member from the list." : confirmAction === "deactivate" ? "This member will be hidden from the public website." : "This member will be made visible on the public website."}</p>
            <div className="confirm-actions"><button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button type="button" className={`btn ${confirmAction === "delete" ? "btn-danger" : "btn-warning"}`} onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleToggleStatus(confirmId)}>Confirm</button></div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="ec-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{modalType === "add" ? "Add Member" : "Edit Member"}</h2><button type="button" className="modal-close" onClick={closeModal}>×</button></div>
            <form className="modal-form" onSubmit={handleSave}>
              <div className="form-section-label">Media</div>
              <div className="form-group">
                <label>Photo</label>
                <div style={{ width: "fit-content" }}>
                  <PhotoUpload
                    value={previewUrl}
                    onChange={handlePhotoChange}
                    onRemove={handleRemovePhoto}
                    inputId="ecPhotoUpload"
                    shape="circle"
                  />
                </div>
              </div>
              <div className="form-section-label">Basic Info</div>
              <div className="form-row">
                <div className="form-group"><label>Full Name *</label><input type="text" value={formData.Full_Name || ""} onChange={e => setFormData({...formData, Full_Name: e.target.value})} required /></div>
                <div className="form-group"><label>Role in Council *</label><select value={formData.Role_in_Council || ""} onChange={e => setFormData({...formData, Role_in_Council: e.target.value})} required><option value="">Select role</option>{roleInCouncilOptions.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Expertise Area</label><input type="text" value={formData.Expertise_Area || ""} onChange={e => setFormData({...formData, Expertise_Area: e.target.value})} placeholder="e.g. Higher Education" /></div>
                <div className="form-group"><label>Display Order</label><input type="number" value={formData.Display_Order || ""} onChange={e => setFormData({...formData, Display_Order: e.target.value})} placeholder="e.g. 1" /></div>
              </div>
              <div className="form-group"><label>Short Description</label><textarea value={formData.Short_Description || ""} onChange={e => setFormData({...formData, Short_Description: e.target.value})} rows="2" /></div>
              <div className="form-section-label">Professional & Contact</div>
              <div className="form-row">
                <div className="form-group"><label>Qualification</label><input type="text" value={formData.Qualification || ""} onChange={e => setFormData({...formData, Qualification: e.target.value})} /></div>
                <div className="form-group"><label>Experience</label><input type="text" value={formData.Experience || ""} onChange={e => setFormData({...formData, Experience: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Email *</label><input type="email" value={formData.Email || ""} onChange={e => setFormData({...formData, Email: e.target.value})} required /></div>
                <div className="form-group"><label>Phone *</label><input type="text" value={formData.Phone || ""} onChange={e => setFormData({...formData, Phone: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Office Address</label><input type="text" value={formData.Office_Address || ""} onChange={e => setFormData({...formData, Office_Address: e.target.value})} placeholder="e.g. Room 101, Admin Block" /></div>
              </div>
              <div className="modal-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="btn btn-primary">Save Member</button></div>
            </form>
          </div>
        </div>
      )}

      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="ec-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>View Member Details</h2><button type="button" className="modal-close" onClick={closeModal}>×</button></div>
            <div className="view-body">
              <div className="view-profile-top">{selectedItem.Media ? <img src={selectedItem.Media} alt="" className="view-photo" /> : <div className="view-photo-placeholder">{(selectedItem.Full_Name || "?").charAt(0)}</div>}<div className="view-profile-info"><h3>{selectedItem.Full_Name}</h3><span className="view-role">{selectedItem.Role_in_Council}</span></div></div>
              <div className="view-section-label">Professional & Contact Details</div>
              <div className="view-grid">
                <div className="view-item"><span>Email</span><strong>{selectedItem.Email || "-"}</strong></div>
                <div className="view-item"><span>Phone</span><strong>{selectedItem.Phone || "-"}</strong></div>
                <div className="view-item"><span>Qualification</span><strong>{selectedItem.Qualification || "-"}</strong></div>
                <div className="view-item"><span>Experience</span><strong>{selectedItem.Experience || "-"}</strong></div>
                <div className="view-item"><span>Expertise Area</span><strong>{selectedItem.Expertise_Area || "-"}</strong></div>
                <div className="view-item"><span>Office Address</span><strong>{selectedItem.Office_Address || "-"}</strong></div>
                <div className="view-item"><span>Display Order</span><strong>{selectedItem.Display_Order || "-"}</strong></div>
              </div>
              <div className="view-footer-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Close</button><button type="button" className="btn btn-primary" onClick={() => { setFormData({...selectedItem}); setModalType("edit"); }}>Edit Member</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveCouncil;